import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faRefresh, faPencil, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Button } from '../../../../ui';

interface SubmissionErrorProps {
    error: string;
    errorDetails?: string;
    failedFormStep?: number;
    canRetry: boolean;
    retryCount?: number;
    maxRetries?: number;
    onRetry: () => void;
    onEdit: (step: number) => void;
    onDismiss: () => void;
}

const STEP_LABELS = ['Image', 'Location & Shop', 'Coffee Type', 'Rating'];

const SubmissionError: React.FC<SubmissionErrorProps> = ({
    error,
    errorDetails,
    failedFormStep,
    canRetry,
    retryCount = 0,
    maxRetries = 3,
    onRetry,
    onEdit,
    onDismiss
}) => {
    // categorize error for better UX
    const getErrorCategory = (err: string): 'network' | 'validation' | 'auth' | 'server' | 'unknown' => {
        const lowerErr = err.toLowerCase();
        if (lowerErr.includes('network') || lowerErr.includes('connection') || lowerErr.includes('timeout')) {
            return 'network';
        }
        if (lowerErr.includes('validation') || lowerErr.includes('required') || lowerErr.includes('invalid')) {
            return 'validation';
        }
        if (lowerErr.includes('auth') || lowerErr.includes('login') || lowerErr.includes('token')) {
            return 'auth';
        }
        if (lowerErr.includes('server') || lowerErr.includes('500')) {
            return 'server';
        }
        return 'unknown';
    };

    const errorCategory = getErrorCategory(error);
    const stepLabel = failedFormStep ? STEP_LABELS[failedFormStep - 1] : null;

    const getErrorSuggestion = (): string => {
        switch (errorCategory) {
            case 'network':
                return 'Check your internet connection and try again.';
            case 'validation':
                return 'Please review your input and correct any issues.';
            case 'auth':
                return 'Your session may have expired. Please log in again.';
            case 'server':
                return 'The server is experiencing issues. Please try again later.';
            default:
                return 'Please try again or contact support if the issue persists.';
        }
    };

    return (
        <div className="space-y-4 p-4 border border-pink/30 bg-pink/5 rounded-xl shadow-sm animate-in slide-in-from-top-2">
            {/* header */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-pink/10">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4 text-pink" />
                    </div>
                    <div>
                        <div className="font-semibold text-foreground">Submission Failed</div>
                        {stepLabel && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                                Error occurred at: {stepLabel} step
                            </div>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1"
                    aria-label="Dismiss error"
                >
                    <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                </button>
            </div>

            {/* error message */}
            <div className="space-y-2">
                <div className="text-sm text-foreground">{error}</div>
                {errorDetails && (
                    <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-sm font-mono">
                        {errorDetails}
                    </div>
                )}
                <div className="text-xs text-muted-foreground">
                    💡 {getErrorSuggestion()}
                </div>
            </div>

            {/* retry info */}
            {canRetry && (
                <div className="text-xs text-muted-foreground">
                    Retry attempts: {retryCount}/{maxRetries}
                </div>
            )}

            {/* actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-pink/20">
                {canRetry && (
                    <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={onRetry}
                        icon={faRefresh}
                        iconPosition="left"
                    >
                        Try Again
                    </Button>
                )}
                
                {failedFormStep && failedFormStep > 1 && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(failedFormStep)}
                        icon={faPencil}
                        iconPosition="left"
                    >
                        Edit {stepLabel}
                    </Button>
                )}

                {/* always allow going back to edit earlier steps */}
                {failedFormStep && failedFormStep > 1 && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(1)}
                    >
                        Start Over
                    </Button>
                )}
            </div>
        </div>
    );
};

export default SubmissionError;
